/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useContext, useEffect, useState } from 'react'

import { EuiCallOut, EuiFlexGroup, EuiFlexItem, EuiLink } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SaasAuthMfaDeviceResponse, SaasAuthResponse } from '@modules/cloud-api/v1/types'
import { filterWeakMfaDevices } from '@modules/mfa-lib/devices'
import type { MfaChallenge } from '@modules/cloud-lib/users/hooks/mfa'
import {
  useDeleteSmsDevicesMutation,
  useVerifySaasCurrentUserMfaChallengeMutation,
} from '@modules/cloud-lib/users/hooks/mfa'
import { useChallengeWebAuthnMutation } from '@modules/cloud-lib/users/hooks/webauthn'
import AuthContext from '@modules/auth/context'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import { getPreferredMfaDevice, setLastUsedMfaDevice } from './lib'
import AuthenticatorForm from './AuthenticatorForm'
import EmailForm from './EmailForm'
import SmsForm from './SmsForm'
import SelectDevice from './SelectDevice'
import WebAuthnForm from './WebAuthnForm'
import { hasExpiredOktaStateTokenError } from './errorUtils'

const MfaForm: React.FunctionComponent<{
  stateId: string
  devices: SaasAuthMfaDeviceResponse[]
  onSuccess: (data: SaasAuthResponse) => void
}> = ({ stateId, devices, onSuccess }) => {
  const [_, ldFlags] = useFlagsWhenLoaded()
  const hasMultiOrgFlag = ldFlags.multiOrg
  const { authContext } = useContext(AuthContext)
  const lastLoginOrganizationId =
    hasMultiOrgFlag && authContext.lastLogin?.method === 'username-and-password'
      ? authContext.lastLogin.organizationId
      : undefined

  const activeDevices = filterWeakMfaDevices({ devices })
  const [device, setDevice] = useState<SaasAuthMfaDeviceResponse | null>(
    getPreferredMfaDevice(activeDevices),
  )

  const verifyMutation = useVerifySaasCurrentUserMfaChallengeMutation()

  const { reset } = verifyMutation

  const deleteSmsDevicesMutation = useDeleteSmsDevicesMutation()

  useEffect(() => {
    reset() // on device change

    return () => {
      reset() // on unmount
    }
  }, [reset, device])

  const onSubmit = (challenge: MfaChallenge) => {
    if (!device) {
      return // sanity
    }

    verifyMutation.mutate(
      {
        deviceId: device.device_id,
        stateId,
        challenge,
        organizationId: lastLoginOrganizationId,
      },
      {
        onSuccess: (data) => {
          setLastUsedMfaDevice(device.device_id)

          // Deleting SMS devices after successful authentication,
          // as they are considered deprecated and soon to be unsupported.
          deleteSmsDevicesMutation.mutate({ devices })

          onSuccess(data)
        },
      },
    )
  }

  const challengeWebAuthnMutation = useChallengeWebAuthnMutation(onSubmit)

  if (activeDevices.length === 0) {
    return (
      <EuiCallOut
        announceOnMount={true}
        title={
          <FormattedMessage
            id='mfaLogin.noMfa.title'
            defaultMessage='No multifactor authentication method is set up for your account'
          />
        }
        color='danger'
        iconType='alert'
      >
        <p>
          <FormattedMessage
            id='mfaLogin.noMfa.instructions'
            defaultMessage={`Multifactor authentication is now mandatory on Elastic Cloud, but you don't have a method set up or the method you were previously using no longer ensures the safety of your account. <contactSupportLink>Contact Support</contactSupportLink> to retrieve access to your account.`}
            values={{
              contactSupportLink: (content) => <a href='mailto:support@elastic.co'>{content}</a>,
            }}
          />
        </p>
      </EuiCallOut>
    )
  }

  const isInvalidToken = hasExpiredOktaStateTokenError(
    verifyMutation.error ?? challengeWebAuthnMutation.error,
  )

  return (
    <EuiFlexGroup direction='column' gutterSize='xl' alignItems='center'>
      <EuiFlexItem>
        {!device && <SelectDevice devices={activeDevices} onSelect={setDevice} />}

        {device?.device_type === 'GOOGLE' && (
          <AuthenticatorForm
            verifyMutation={verifyMutation}
            onSubmit={onSubmit}
            isInvalidToken={isInvalidToken}
          />
        )}
        {device?.device_type === 'EMAIL' && (
          <EmailForm
            device={device}
            stateId={stateId}
            verifyMutation={verifyMutation}
            onSubmit={onSubmit}
            isInvalidToken={isInvalidToken}
          />
        )}
        {device?.device_type === 'SMS' && (
          <SmsForm
            device={device}
            stateId={stateId}
            verifyMutation={verifyMutation}
            onSubmit={onSubmit}
            isInvalidToken={isInvalidToken}
          />
        )}
        {device?.device_type === 'WEBAUTHN' && (
          <WebAuthnForm
            device={device}
            stateId={stateId}
            verifyMutation={verifyMutation}
            challengeMutation={challengeWebAuthnMutation}
            isInvalidToken={isInvalidToken}
          />
        )}
      </EuiFlexItem>
      {device && activeDevices.length > 1 && !isInvalidToken && (
        <EuiFlexItem>
          <EuiLink onClick={() => setDevice(null)}>
            <FormattedMessage
              id='mfaLogin.changeDevice'
              defaultMessage='Authenticate with a different method'
            />
          </EuiLink>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  )
}

export default MfaForm
